import { useState } from 'react'
import * as Yup from 'yup'
import Box from '@mui/material/Box'
import Paper from '@/components/generics/Paper'
import Typography from '@/components/common/Typography'
import { Button } from '@/components/common/Buttons'

import { useForm } from '@/hooks'
import useCrud from '@/hooks/useCrud'
import useToast from '@/hooks/useToast'
import { useAuth } from '@/hooks/useAuth'
import { useMediaQueryDown } from '@/hooks/useMediaQuery'
import { API_COURIER } from '@/constants/routes'
import { buildEnabledDays, normalizeWorkingHours } from '@/utils/schedule'
import Schedule from '@/pages/Profile/Components/Schedule'
import { useStyles } from '@/pages/Profile/styles'
import { CourierSchedule } from '@/common/types'
import { DayEnum } from '@/common/enums'

type ScheduleFormValues = {
    workingHours: CourierSchedule[]
    enabledDays: Record<DayEnum, boolean>
}

const schema = Yup.object().shape({
    workingHours: Yup.array().test('required-times', 'Start and end time are required for selected days.', function (value) {
        const enabledDays = (this.parent.enabledDays || {}) as Record<DayEnum, boolean>
        const normalized = normalizeWorkingHours((value || []) as CourierSchedule[])
        return Object.values(DayEnum).every((day) => {
            if (!enabledDays[day]) return true
            const entry = normalized.find((item) => item.day === day)
            return Boolean(entry?.startTime) && Boolean(entry?.endTime)
        })
    }),
})

export const WorkSchedule = () => {
    const { classes } = useStyles()
    const { user, setUser } = useAuth()
    const isSmallScreen = useMediaQueryDown({ breakpoint: 'sm' })
    const [apiRequest] = useCrud('PUT')
    const [toastSuccess] = useToast()
    const [loading, setLoading] = useState(false)

    const onSubmit = async (values: ScheduleFormValues) => {
        try {
            setLoading(true)
            const response = await apiRequest(API_COURIER.UPDATE_COURIER, {
                workingHours: values.workingHours,
            })
            if (user) {
                setUser({ ...user, ...response })
            }
            toastSuccess('Schedule Updated')
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const { values, errors, setFieldValue, handleSubmit } = useForm(
        schema,
        {
            workingHours: normalizeWorkingHours(user?.workingHours ?? []),
            enabledDays: buildEnabledDays(user?.workingHours ?? []),
        },
        onSubmit,
        { enableReinitialize: true },
    )

    return (
        <div className='page-content'>
            <Paper px={isSmallScreen ? 2 : 10} py={1}>
                <Box sx={{ backgroundColor: 'white', marginTop: '15px' }}>
                    <Typography className='text-left' gutter='bottomSpace' variant={isSmallScreen ? 'h5' : 'h4'} >
                        Work Schedule
                    </Typography>
                    <form action="#" onSubmit={handleSubmit}>
                        <Schedule
                            values={values}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            enabledDays={values.enabledDays}
                            onEnabledDaysChange={(next) => setFieldValue('enabledDays', next)}
                        />
                        <div className='flex-center'>
                            <Button
                                className={classes.continueButton}
                                variant="contained"
                                pending={loading}
                                color="secondary"
                                type="submit"
                            >
                                Save
                            </Button>
                        </div>
                    </form>
                </Box>
            </Paper>
        </div>
    )
}
